import { useState, useEffect, useCallback } from 'react'

export interface TourStep {
  id: string
  target: string
  title: string
  body: string
  position: 'top' | 'bottom' | 'left' | 'right'
  unnumbered?: boolean
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'athena-welcome',
    target: '.jua-athena-welcome',
    title: 'Meet Athena, your AI assistant',
    body: 'For researching markets, brainstorming strategies, and creating dashboards.',
    position: 'top',
  },
  {
    id: 'athena-button',
    target: '.athena-btn',
    title: 'Chat with Athena anytime',
    body: 'Brainstorm, strategize, edit widgets, anytime, anywhere.',
    position: 'bottom',
  },
  {
    id: 'workspaces',
    target: '.jua-topbar-new-ws',
    title: 'Create custom dashboards',
    body: 'Choose from a template, or combine charts, maps, and tables.',
    position: 'bottom',
  },
  {
    id: 'notifications',
    target: '.app-topbar .flex-shrink-0 > .relative:first-child',
    title: 'Get real-time alerts',
    body: 'Stay informed when prices spike or models shift unexpectedly.',
    position: 'bottom',
  },
  {
    id: 'managed-pages',
    target: '.jua-sidebar-pages-top',
    title: 'Switch between pages',
    body: 'Jump to Meteorologist, Short Term Trader, Workspaces, and more.',
    position: 'right',
  },
  {
    id: 'pinned-workspaces',
    target: '.jua-sidebar-pinned',
    title: 'Pin your top workspaces',
    body: 'Drag your favorites here for instant one-click access.',
    position: 'right',
  },
  {
    id: 'user-settings',
    target: '.jua-user-footer-btn',
    title: 'Your profile & preferences',
    body: 'Change your theme, display units, onboarding preferences, and replay this tour anytime from this menu.',
    position: 'right',
  },
  {
    id: 'athena-home',
    target: '.jua-athena-link',
    title: 'Athena is your home base',
    body: 'Come back here anytime to ask questions, get market insights, or start a new analysis.',
    position: 'right',
    unnumbered: true,
  },
]

export function useProductTour() {
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    const isCompleted = localStorage.getItem('jua-tour-completed') === 'true'
    if (!isCompleted) {
      const timer = setTimeout(() => {
        setIsActive(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  // Listen for restart event from other components (e.g. user menu)
  useEffect(() => {
    const handleRestart = () => {
      setCurrentStep(0)
      setIsActive(true)
    }
    window.addEventListener('jua-restart-tour', handleRestart)
    return () => window.removeEventListener('jua-restart-tour', handleRestart)
  }, [])

  const completeTour = useCallback(() => {
    setIsActive(false)
    localStorage.setItem('jua-tour-completed', 'true')
  }, [])

  const skipTour = useCallback(() => {
    setIsActive(false)
    localStorage.setItem('jua-tour-completed', 'true')
  }, [])

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      completeTour()
    }
  }, [currentStep, completeTour])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [currentStep])

  return {
    isActive,
    currentStep,
    steps: TOUR_STEPS,
    completeTour,
    skipTour,
    nextStep,
    prevStep,
  }
}
